import React, { useState, useEffect, useRef } from 'react';
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from "../../common/ReusableDeleteDialog";
import { getRequest, postRequest } from "../../services/apiService"; // Removed putRequest and deleteRequest
import { Trash2, Edit, X } from 'lucide-react';

const CompartmentActivity = () => {
  const toast = useRef(null);
  const [activities, setActivities] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status_db: 1,
  });

  // Delete confirmation form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Delete form state
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  // Form fields configuration
  const formFields = [
    {
      name: "name",
      label: "Activity Name",
      type: "text",
      required: true,
      placeholder: "Enter activity name"
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter activity description"
    }
  ];

  // Debounced search state
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Fetch activities from API
  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`master/compartment-activity/?search=${encodeURIComponent(search)}&page=${currentPage}`);
      setActivities(response.results?.data || []);
      setTotalRecords(response.count || 0);
    } catch (error) {
      showNotification('Failed to fetch activities', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to use fetchActivities
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchActivities();
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const showNotification = (message, type) => {
    toast.current.show({
      severity: type,
      summary: type === 'success' ? 'Success' : type === 'warning' ? 'Warning' : 'Error',
      detail: message,
      life: 3000
    });
  };

  const handleSave = async () => {
    if (!formData.name) {
      showNotification('Please enter activity name', 'error');
      return;
    }

    try {
      if (currentActivity) {
        // UPDATE: Use POST with id in payload
        await postRequest(`master/compartment-activity/`, {
          ...formData,
          id: currentActivity.id
        });
        showNotification('Activity updated successfully!', 'success');
      } else {
        // CREATE: Original POST remains the same
        await postRequest('master/compartment-activity/', formData);
        showNotification('Activity created successfully!', 'success');
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({ name: '', description: '' });
      await fetchActivities(); // Refresh the table
    } catch (error) {
      console.error('Error saving activity:', error);
      showNotification('Failed to save activity', 'error');
    }
  };

  // Open add modal
  const openAddModal = () => {
    setCurrentActivity(null);
    setFormData({ name: "", description: "" });
    setShowAddModal(true);
  };

  // Open edit modal
  const openEditModal = (activity) => {
    setCurrentActivity(activity);
    setFormData({
      name: activity.name,
      description: activity.description || ""
    });
    setShowEditModal(true);
  };

  // Delete activity - now using POST with status_db=3
  const deleteActivity = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      showNotification("Please type 'DELETE' to confirm", 'error');
      return;
    }

    try {
      // DELETE: Use POST with status_db=3 and id
      await postRequest(`master/compartment-activity/`, {
        id: activityToDelete.id,
        status_db: 3
      });
      showNotification('Activity deleted successfully', 'success');
      setShowDeleteModal(false);
      setActivityToDelete(null);
      setDeleteFormData({ confirmation: "" });
      await fetchActivities(); // Refresh the table
    } catch (error) {
      console.error("Error deleting activity:", error);
      showNotification('Failed to delete activity', 'error');
    }
  };

  // Open delete confirmation
  const openDeleteModal = (activity) => {
    setActivityToDelete(activity);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  // Handle pagination
  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  // Table columns
  const columnFields = [
    { field: "name", header: "Activity Name" },
    { field: "description", header: "Description" },
  ];

  // Action items for table
  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData),
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4">
      <Toast ref={toast} position="top-right" />
      <div className="max-w-full mx-auto">
        {/* Table Section */}
        <div className="mt-0">
          <ReusableTable
            title="Compartment Activities"
            data={activities}
            setData={setActivities}
            columnFields={columnFields}
            setAddNewDialogVisible={openAddModal}
            actionMenu={true}
            actionItems={actionItems}
            loading={loading}
            totalRecords={totalRecords}
            rows={rowsPerPage}
            onPageChange={onPageChange}
            first={(currentPage - 1) * rowsPerPage}
            emptyMessage="No equipment activities found. Add new activities to get started."
            searchValue={search}
            onSearchChange={handleTableSearch}
          />
        </div>

        {/* Add/Edit Activity Modal using ReusableForm with explicit wrapper */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-lg animate-fade-in">
              <ReusableForm
                headerText={showEditModal ? "Edit Activity" : "Add New Activity"}
                formData={formData}
                setFormData={setFormData}
                fields={formFields}
                onSubmit={handleSave}
                onHide={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={deleteActivity}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Activity"
          description={`Are you sure you want to delete activity "${activityToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
        />
      </div>
    </div>
  );
};

export default CompartmentActivity;